import api, {callsApi} from 'sources/api';
import FileUploadScorecardActions from 'actions/scorecards/FileUploadScorecardActions';
import {Map, List} from 'immutable';

const interceptResponse = (response, action, args) => {
  if (
    action === FileUploadScorecardActions.fetchScorecardsSuccess ||
    action === FileUploadScorecardActions.fetchScorecardResultsSuccess ||
    action === FileUploadScorecardActions.submitScorecardResultsSuccess
  ) {
    return {args, response};
  } else {
    return response;
  }
};

/**
 * `FileUploadScorecardSource` implements interactions with scorecards-related endpoints
 */
const FileUploadScorecardSource = {
  fetchScorecards: {
    remote(state, fileUploadId) {
      return api.get(`/file/${fileUploadId}/scorecards`);
    },
    interceptResponse: interceptResponse,
    loading: FileUploadScorecardActions.fetchScorecardsLoading,
    success: FileUploadScorecardActions.fetchScorecardsSuccess,
    error: FileUploadScorecardActions.fetchScorecardsError,
  },

  fetchScorecardResults: {
    remote(state, fileUploadId) {
      return callsApi.get(`/scorecards/results/call/${fileUploadId}`);
    },
    interceptResponse: interceptResponse,
    loading: FileUploadScorecardActions.fetchScorecardResultsLoading,
    success: FileUploadScorecardActions.fetchScorecardResultsSuccess,
    error: FileUploadScorecardActions.fetchScorecardResultsError,
  },

  submitScorecardResults: {
    remote(state, fileUploadId, reviewerId, scorecard) {
      let data = Map(); // the JSON request body we are going to build in this function

      const questionsToAnswers = state.getIn(['answers', fileUploadId, scorecard.id]);
      const questionsToComments = state.getIn(['comments', fileUploadId, scorecard.id]);
      const questionsToTaggedUsers = state.getIn(
        ['taggedUsers', fileUploadId, scorecard.id],
        Map()
      );
      const questionsToReasons = state.getIn(['reasons', fileUploadId, scorecard.id]);

      let answers = List();
      questionsToAnswers.forEach((answer, question) => {
        const comment = questionsToComments ? questionsToComments.get(question) : null;
        const reason = questionsToReasons ? questionsToReasons.get(question, null) : null;

        // the backend knows how to deal with answer = 'n/a'
        answers = answers.push({
          questionId: question,
          score: answer,
          comment: comment,
          reason: reason,
          taggedUsers: questionsToTaggedUsers.get(question, List()).map(user => user.id),
        });
      });

      const answeredQuestionIds = answers.map(answer => answer.questionId);
      const unansweredQuestions = scorecard.questions.filterNot(question =>
        answeredQuestionIds.includes(question.id)
      );
      unansweredQuestions.forEach(question => {
        answers = answers.push({
          questionId: question.id,
          score: 'n/a',
          taggedUsers: List(),
        });
      });

      // eslint-disable-next-line
      data = data.set('answers', answers.toArray());
      const scorecardJson = {
        scorecardId: scorecard.id,
        reviewerId: reviewerId,
        fileUploadId: fileUploadId,
        questionResults: answers.toArray(),
      };
      return callsApi.postJson(`/scorecards/${scorecard.id}/results`, scorecardJson).then(
        // the kotlin save results api does not return the list of scorecards after update, so we get the list again
        scorecardResultId => api.get(`/file/${fileUploadId}/scorecards`)
      );
    },
    interceptResponse: interceptResponse,
    loading: FileUploadScorecardActions.submitScorecardResultsLoading,
    success: FileUploadScorecardActions.submitScorecardResultsSuccess,
    error: FileUploadScorecardActions.submitScorecardResultsError,
  },
};

export default FileUploadScorecardSource;
